import { AppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/AppConfig';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { fireApiErrorSentry } from '@porterin/component-library/src/ts-utils/analytics/sentry/SentryHelpers';
import axios, { AxiosResponse } from 'axios';

import { getUtmParams } from '../../components/FareEstimateUtmParamsHelper';
import { FareEstimateValidatorApiModel } from '../api-models/FareEstimateValidatorApiModel';
import { API } from '../configs/Config';
import { FareEstimateForm } from '../entities/FareEstimateForm';
import { FareEstimateValidator } from '../entities/FareEstimateValidator';
import { FareEstimateError } from '../entities/FareEstimateValidatorError';
import { toFareEstimateResponse } from '../entity-mappers/FareEstimateValidatorEntityMapper';
import { toFareEstimateError } from '../entity-mappers/FareEstimateValidatorErrorMapper';
import { getLocaleHeaders } from '@porterin/component-library/src/utils/LocaleHelper';
import { isDesktop } from '../../components/FareEstimateHelper';
import { getCookie } from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';

const Config: AppConfig = getAppConfig();

const API_URL = `${Config.oms_url}/${API.fare_estimate_v2}`;

export function encodePayload(
  form: FareEstimateForm,
  isDesktop: boolean,
): Record<string, string> {
  const {
    pickupAddress,
    dropAddress,
    name,
    mobile,
    requirement,
    serviceCategory,
  } = form;

  return {
    from_address_landmark: pickupAddress.landmark,
    to_address_landmark: dropAddress.landmark,
    from_address_lat: pickupAddress.location.lat.toString(),
    from_address_long: pickupAddress.location.lng.toString(),
    to_address_lat: dropAddress.location.lat.toString(),
    to_address_long: dropAddress.location.lng.toString(),
    customer_name: name,
    customer_mobile: mobile,
    user_type: '/',
    brand: 'porter',
    frequency: requirement,
    service_category: serviceCategory,
    is_device_mobile: isDesktop ? 'false' : 'true',
    source_url: window.location.href,
  };
}

export function appendPayloadToUrl(
  url: string,
  payload: Record<string, string>,
): string {
  const queryParams = new URLSearchParams(payload).toString();
  const utmParams = getUtmParams();
  return `${url}?${queryParams}&${utmParams}`;
}

export function isHandledFareEstimateError(error: FareEstimateError) {
  if (error.formError?.trim()) return true;

  const fieldErrors = error.fieldErrors;
  if (!fieldErrors) return false;

  return Object.values(fieldErrors).some(
    val => typeof val === 'string' && val.trim().length > 0,
  );
}

async function submitFareEstimate(
  formData: FareEstimateForm,
): Promise<FareEstimateValidator | FareEstimateError> {
  try {
    const payload = encodePayload(formData, isDesktop());
    const apiUrlWithParams = appendPayloadToUrl(API_URL, payload);
    const response: AxiosResponse<FareEstimateValidatorApiModel> =
      await axios.post(apiUrlWithParams, undefined, {
        headers: {
          ...getLocaleHeaders(),
          'Web-Tracking-Id': getCookie('web_tracking_id') ?? null,
        },
      });

    return toFareEstimateResponse(response.data);
  } catch (error: unknown) {
    const errorData =
      axios.isAxiosError(error) && error.response?.data
        ? error.response.data
        : {};
    let fareEstimateError = toFareEstimateError(errorData);
    if (!isHandledFareEstimateError(fareEstimateError)) {
      fireApiErrorSentry(error, 'submitFareEstimate');
    }
    return fareEstimateError;
  }
}

export { submitFareEstimate };
